import type { KhoangAi } from '../../chung/nghiep-vu/khoang.ts';
import { mocDau } from '../../chung/nghiep-vu/moc-chon.ts';
import { TRONG } from '../../chung/nghiep-vu/so-thong-ke.ts';
import {
  LOAI_TOKEN,
  type BocToken,
  type BuocVet,
  type ChuoiVet,
  type DongTrace,
  type LoaiToken,
  type LuotGoi,
  type TrangLuotGoi,
  type TrangTrace,
} from '../../chung/nghiep-vu/types-vet.ts';
import type { ThongKeAi } from '../../chung/nghiep-vu/types-thong-ke.ts';

/**
 * Phép tính thuần của màn Sử dụng AI — port `vet/format.ts`, `vet/gop.ts` và phần không vẽ của `vet/ChuoiBuoc.tsx`,
 * `vet/NhanTinCay.tsx` native.
 *
 * Luật xuyên suốt: **không có số thì trả `TRONG`, không trả `0`**. Màn này là màn đối soát chi phí, và một `0` bịa ra
 * chỗ thiếu dữ liệu là con số sẽ bị chụp màn hình đưa vào báo cáo.
 *
 * Khác native: số đi theo ngôn ngữ (`locale`, ADR-UI-0010) thay vì ghim `vi-VN`.
 */

const LOCALE_MAC_DINH = 'vi-VN';
const MOT_GIAY_MS = 1000;

/**
 * Độ dài thời gian. Dưới một giây thì mili-giây, từ một giây thì giây — "1.247 ms" và "4.902 ms" đứng cạnh nhau là hai
 * con số mắt không so được. Đơn vị `ms`/`s` là ký hiệu SI, giống nhau ở mọi ngôn ngữ.
 */
export function khoangMs(giaTri: number | null | undefined, locale: string = LOCALE_MAC_DINH): string {
  if (giaTri === null || giaTri === undefined || !Number.isFinite(giaTri)) return TRONG;
  if (Math.abs(giaTri) < MOT_GIAY_MS) {
    return `${Math.round(giaTri).toLocaleString(locale, { maximumFractionDigits: 0 })} ms`;
  }
  return `${(giaTri / MOT_GIAY_MS).toLocaleString(locale, { maximumFractionDigits: 2 })} s`;
}

export interface PhanToken {
  loai: LoaiToken;
  /** `null` = nhà cung cấp không báo loại này. */
  giaTri: number | null;
}

export interface BocBay {
  phan: PhanToken[];
  /** Tổng của các loại CÓ báo, hoặc `null` khi không loại nào có. */
  tongBay: number | null;
  soKhongBao: number;
}

/**
 * Bóc một `token` thành đủ bảy loại theo thứ tự đã chốt, kèm tổng để người đọc kiểm phép phân hoạch.
 *
 * Duyệt `LOAI_TOKEN` chứ không duyệt khoá của JSON: khoá thiếu và khoá bằng `null` phải cho ra cùng một kết quả "không
 * báo", và một loại mới mà server thêm vào không tự lọt lên giao diện thành một hàng không ai dịch.
 */
export function bocBay(token: BocToken | null | undefined): BocBay {
  let tongBay: number | null = null;
  let soKhongBao = 0;
  const phan = LOAI_TOKEN.map<PhanToken>((loai) => {
    const tho = token?.[loai];
    const giaTri = typeof tho === 'number' && Number.isFinite(tho) ? tho : null;
    if (giaTri === null) soKhongBao += 1;
    else tongBay = (tongBay ?? 0) + giaTri;
    return { loai, giaTri };
  });
  return { phan, tongBay, soKhongBao };
}

/**
 * Bảy loại cộng lại có khớp `tong_token` không. `null` khi chưa đủ dữ liệu để kết luận — giao diện không khẳng định gì.
 * Chỉ `false` mới là một phát hiện: phép phân hoạch của mục 7.3 bị vỡ ở đúng lượt gọi này.
 */
export function khopPhanHoach(boc: BocBay, tongToken: number | null): boolean | null {
  if (tongToken === null || boc.tongBay === null) return null;
  return boc.tongBay === tongToken;
}

/**
 * Gộp các trang của một lượt cuộn vô hạn, bỏ bản ghi trùng. Trùng là chuyện bình thường của phân trang keyset trên dữ
 * liệu đang được ghi thêm, và ở màn này một chuỗi hiện hai lần nghĩa là người đọc cộng token của nó hai lần. Giữ thứ tự
 * của server và giữ bản gặp TRƯỚC.
 */
function gop<T, K>(trang: readonly { du_lieu: T[] }[] | undefined, khoaCua: (m: T) => K): T[] {
  if (!trang) return [];
  const daCo = new Set<K>();
  const ra: T[] = [];
  for (const mot of trang) {
    for (const dong of mot.du_lieu) {
      const khoa = khoaCua(dong);
      if (daCo.has(khoa)) continue;
      daCo.add(khoa);
      ra.push(dong);
    }
  }
  return ra;
}

export function gopTrace(trang: readonly TrangTrace[] | undefined): DongTrace[] {
  return gop(trang, (d) => d.trace_id);
}

export function gopLuotGoi(trang: readonly TrangLuotGoi[] | undefined): LuotGoi[] {
  return gop(trang, (d) => d.event_id);
}

export const MA_LLM_GOI = 'llm.goi';

/** Bước là một lượt gọi mô hình — có mã `llm.goi`, hoặc mang token / mô hình dù bên gửi ghi mã khác. */
export function laLlm(buoc: Pick<BuocVet, 'hanh_dong' | 'token' | 'mo_hinh'>): boolean {
  return buoc.hanh_dong === MA_LLM_GOI || buoc.token !== null || buoc.mo_hinh !== null;
}

/** Tên tính năng dạng người đọc được, lùi về mã khi bên gửi không khai nhãn. */
export function nhanTinhNang(buoc: Pick<BuocVet, 'du_lieu_them' | 'tinh_nang'>): string | null {
  const nhan = buoc.du_lieu_them['tinh_nang'];
  return typeof nhan === 'string' && nhan.trim() ? nhan.trim() : buoc.tinh_nang;
}

/** Bề rộng trục waterfall: đủ chứa cả bước cuối lẫn độ trễ của nó. Tối thiểu `1` để không chia cho 0. */
export function beRongTruc(chuoi: Pick<ChuoiVet, 'buoc' | 'keo_dai_ms'>): number {
  const het = chuoi.buoc.reduce((a, b) => Math.max(a, b.lech_ms + (b.do_tre_ms ?? 0)), 0);
  return Math.max(chuoi.keo_dai_ms, het, 1);
}

/** Bước tốn token nhất của chuỗi — mẫu số của thanh token. `0` khi không bước nào có token. */
export function tokenLonNhat(chuoi: Pick<ChuoiVet, 'buoc'>): number {
  return chuoi.buoc.reduce((a, b) => Math.max(a, b.tong_token ?? 0), 0);
}

/**
 * Nhãn "chưa có giá" ở mức GOM (chuỗi, luồng): tách thiếu dòng giá với thiếu tỷ giá khi máy chủ trả hai cờ. Máy chủ cũ
 * không trả thì giữ nhãn chung như trước.
 */
export function coGiaThieu(
  coChuaCoGia: boolean | null | undefined,
  coThieuTyGia?: boolean | null,
  coThieuDongGia?: boolean | null,
): { chuaCoGia: boolean; thieuTyGia: boolean } {
  if (coChuaCoGia !== true) return { chuaCoGia: false, thieuTyGia: false };
  const daTach = coThieuTyGia != null || coThieuDongGia != null;
  return { chuaCoGia: daTach ? coThieuDongGia === true : true, thieuTyGia: coThieuTyGia === true };
}

/**
 * Kiểm bất biến của QĐ-14 trên số liệu VỪA NHẬN: bảy lát cộng đúng `tong_token`. Đếm cả số mốc lệch chứ không chỉ tổng
 * kỳ: hai lượt lệch ngược dấu nhau có thể triệt tiêu ở tổng kỳ mà vẫn là hai lượt quy đổi sai.
 */
export function kiemBayLoai(
  du: Pick<ThongKeAi, 'tong' | 'theo_thoi_gian'> | undefined,
): { khop: boolean; bay: number; soMocLech: number } {
  if (!du) return { khop: true, bay: 0, soMocLech: 0 };
  const bay = LOAI_TOKEN.reduce((cong, loai) => cong + du.tong[`token_${loai}`], 0);
  const soMocLech = du.theo_thoi_gian.filter(
    (hang) => LOAI_TOKEN.reduce((cong, loai) => cong + hang[`token_${loai}`], 0) !== hang.tong_token,
  ).length;
  return { khop: bay === du.tong.tong_token && soMocLech === 0, bay, soMocLech };
}

/**
 * Khoảng "Tuỳ chọn" người dùng chọn có bị `thamSoKhoang` kẹp về 90 ngày không — để nói ra thay vì để số liệu lặng lẽ
 * ngắn hơn khoảng đã chọn. `tu` là mốc dưới ĐÃ tính (ISO).
 */
export function khoangBiKep(khoang: KhoangAi, tu: string): boolean {
  if (khoang.moc !== 'tuy_chon' || !khoang.tu) return false;
  const chon = mocDau(khoang.tu);
  const thuc = Date.parse(tu);
  return Number.isFinite(chon) && Number.isFinite(thuc) && chon < thuc;
}
